import type { Agent } from '@smartsupp/websocket-client-visitor'
import { derived } from 'svelte/store'

import { botIdentities } from '@/modules/botIdentities/store'
import { assignedAgents, isChatOpened, isWidgetOnline } from '@/modules/chat'

import { messageWithBotIdentity } from '../botIdentities'
import { activeAgentsByGroup, activeOnlineAgentsByGroup } from './agents'

type Identity = Agent & {
	isBot?: boolean
	isActive?: boolean
}

const emptyAgent: Identity = {
	id: '',
	avatar: '',
	description: '',
	disabled: false,
	fullname: '',
	groups: [],
	status: 'online',
}

export const groupedAgents = derived(
	[
		activeAgentsByGroup,
		activeOnlineAgentsByGroup,
		assignedAgents,
		isWidgetOnline,
		botIdentities,
		messageWithBotIdentity,
		isChatOpened,
	],
	([
		$activeAgentsByGroup,
		$activeOnlineAgentsByGroup,
		$assignedAgents,
		$isWidgetOnline,
		$botIdentities,
		$messageWithBotIdentity,
		$isChatOpened,
	]): Identity[] => {
		const botIdentities: Identity[] = Object.values($botIdentities).map((i) => {
			return {
				...emptyAgent,
				fullname: i.name ?? '',
				id: i.id,
				avatar: i.avatarUrl ?? '',
				isBot: true,
				isActive: i.isActive,
			}
		})

		const activeBotIdentities = botIdentities.filter((i) => i.isActive)

		if ($assignedAgents.length > 0) {
			const reversedAgents = [...$assignedAgents].reverse()
			if (activeBotIdentities.length > 0) {
				return [...activeBotIdentities, ...reversedAgents]
			}
			if ($messageWithBotIdentity && !$isChatOpened) {
				const identity = botIdentities.find((i) => i.id === $messageWithBotIdentity.id)
				if (identity) return [identity, ...reversedAgents]
			}
			return reversedAgents
		}

		if ($messageWithBotIdentity && !$isChatOpened) {
			const identity = botIdentities.find((i) => i.id === $messageWithBotIdentity.id)
			return identity ? [identity] : [{ ...emptyAgent, isBot: true }]
		}

		if ($isWidgetOnline) {
			if ($isChatOpened) {
				return $activeOnlineAgentsByGroup.length > 0 ? $activeOnlineAgentsByGroup : $activeAgentsByGroup
			}
			return $activeOnlineAgentsByGroup.length === 0 && activeBotIdentities.length === 0
				? [emptyAgent]
				: [...activeBotIdentities, ...$activeOnlineAgentsByGroup]
		}

		return $activeAgentsByGroup
	},
)
